import { useState } from 'react';
import { FileArchive, Grid3x3, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useBatch } from '../../store/batchStore';
import { buildContactSheet, exportBatchZip, type FolderStructure } from '../../bulk/exportBatch';
import { downloadBlob } from '../../core/export/download';
import type { RasterFormat } from '../../core/export/download';
import { Segmented, Select, Toggle } from '../controls/Basics';
import { Slider } from '../controls/Slider';

export function BatchExportBar(): JSX.Element {
  const variants = useBatch((s) => s.variants);
  const config = useBatch((s) => s.config);
  const setConfig = useBatch((s) => s.setConfig);
  const filter = useBatch((s) => s.filter);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [template, setTemplate] = useState('{index}_{technique}_{word}_{seed}');
  const [folders, setFolders] = useState<FolderStructure>('flat');
  const [fullResolution, setFullResolution] = useState(true);
  const [manifest, setManifest] = useState(true);
  const [csv, setCsv] = useState(true);
  const [presets, setPresets] = useState(false);
  const [sheetColumns, setSheetColumns] = useState(6);

  const selected = variants.filter((v) => !v.hidden && (filter !== 'favourites' || v.favourite));
  const exportable = selected.filter((v) => v.blob || fullResolution);

  const runZip = async (): Promise<void> => {
    setBusy(true);
    setStatus('Vorbereiten…');
    const signal = { cancelled: false };
    try {
      const { blob, count } = await exportBatchZip({
        variants: selected,
        format: config.format === 'webp' ? 'png' : config.format,
        quality: config.quality,
        scale: config.exportScale,
        filenameTemplate: template,
        folders,
        includeManifest: manifest,
        includeCsv: csv,
        includePresets: presets,
        fullResolution,
        signal,
        onProgress: (done, total, label) => setStatus(`${done} / ${total} — ${label}`),
      });
      downloadBlob(blob, `typo-batch-${new Date().toISOString().slice(0, 10)}.zip`);
      setStatus(`${count} Bilder · ${(blob.size / 1024 / 1024).toFixed(1)} MB`);
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runSheet = async (): Promise<void> => {
    setBusy(true);
    setStatus('Kontaktbogen…');
    try {
      const blob = await buildContactSheet(selected, sheetColumns, 260, true);
      downloadBlob(blob, `kontaktbogen-${new Date().toISOString().slice(0, 10)}.jpg`);
      setStatus(`${(blob.size / 1024 / 1024).toFixed(1)} MB`);
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (variants.length === 0) return <></>;

  return (
    <div className="shrink-0 border-t border-ink-800 bg-ink-900">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-ink-850"
      >
        <FileArchive size={12} className="text-ink-400" />
        <span className="panel-label flex-1">Export — {exportable.length} Bilder</span>
        {status && <span className="truncate font-mono text-[10px] text-accent-500">{status}</span>}
      </button>

      {open && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-ink-850 px-3 py-2">
          <div>
            <div className="flex items-center gap-2 py-1">
              <span className="w-[86px] shrink-0 text-[11px] text-ink-300">Format</span>
              <div className="min-w-0 flex-1">
                <Segmented
                  size="sm"
                  value={config.format === 'webp' ? 'png' : config.format}
                  onChange={(format: RasterFormat) => setConfig({ format })}
                  options={[
                    { value: 'png', label: 'PNG' },
                    { value: 'jpeg', label: 'JPG' },
                  ]}
                />
              </div>
            </div>

            <Toggle
              checked={fullResolution}
              onChange={setFullResolution}
              label="In voller Auflösung neu rendern"
              tooltip="Aus: die Galerie-Thumbnails werden verpackt — schnell, aber klein."
            />
            {fullResolution && (
              <Slider
                label="Skalierung"
                value={config.exportScale}
                onChange={(exportScale) => setConfig({ exportScale: Math.round(exportScale) })}
                min={1}
                max={6}
                step={1}
                unit="×"
                precision={0}
              />
            )}

            <div className="flex items-center gap-2 py-1">
              <span className="w-[86px] shrink-0 text-[11px] text-ink-300">Ordner</span>
              <div className="min-w-0 flex-1">
                <Select
                  value={folders}
                  onChange={(f: FolderStructure) => setFolders(f)}
                  options={[
                    { value: 'flat', label: 'Flach' },
                    { value: 'technique', label: 'Nach Technik' },
                    { value: 'word', label: 'Nach Wort' },
                    { value: 'palette', label: 'Nach Palette' },
                  ]}
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 py-1">
              <span className="w-[86px] shrink-0 text-[11px] text-ink-300">Dateiname</span>
              <input
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                spellCheck={false}
                className="field-input font-mono text-[10px]"
              />
            </div>
            <p className="pb-1 font-mono text-[9px] leading-relaxed text-ink-600">
              {'{index} {technique} {word} {seed} {palette} {font} {size}'}
            </p>
            <Toggle checked={manifest} onChange={setManifest} label="manifest.json (alle Parameter)" />
            <Toggle checked={csv} onChange={setCsv} label="index.csv" />
            <Toggle checked={presets} onChange={setPresets} label="Preset je Bild" />
          </div>

          <div className="col-span-2 flex items-center gap-2 border-t border-ink-850 pt-2">
            <button
              type="button"
              onClick={() => void runZip()}
              disabled={busy || exportable.length === 0}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
                busy || exportable.length === 0
                  ? 'bg-ink-750 text-ink-500'
                  : 'bg-accent-500 text-ink-950 hover:bg-accent-400',
              )}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <FileArchive size={12} />}
              ZIP herunterladen
            </button>

            <button
              type="button"
              onClick={() => void runSheet()}
              disabled={busy}
              className="ghost-button gap-1.5 rounded-md bg-ink-800 px-3 py-1.5 text-[12px]"
            >
              <Grid3x3 size={12} />
              Kontaktbogen
            </button>
            <input
              type="number"
              min={2}
              max={20}
              value={sheetColumns}
              onChange={(e) => setSheetColumns(Math.max(2, Number(e.target.value) || 6))}
              className="field-input w-14 text-center font-mono text-[11px]"
              title="Spalten im Kontaktbogen"
            />

            <span className="ml-auto text-[10px] text-ink-500">
              {fullResolution
                ? 'Jedes Bild wird einzeln neu gerendert und direkt ins Archiv geschrieben.'
                : 'Verpackt die vorhandenen Vorschaubilder.'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
